import { useMemo, useRef, useState } from 'react';

import type { TermAccordionProps } from '@/app/components/sections/service/service-center/terms/types';
import { Accordion } from '@/app/components/text-and-data-display/accordion';
import { AccordionGroup } from '@/app/components/text-and-data-display/accordion-group';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';

export const TermAccordion = ({
  accordionMode = 'individual',
  items = [],
  enableScrollIntoView = false,
  defaultExpandedIds,
  testId = 'terms',
  ...props
}: TermAccordionProps) => {
  const accordionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const expandedIds = useMemo<string[]>(() => {
    if (defaultExpandedIds === null) return [];

    if (!defaultExpandedIds || defaultExpandedIds.length === 0) {
      return items[0]?.id ? [items[0].id] : [];
    }

    return defaultExpandedIds;
  }, [items, defaultExpandedIds]);

  const [openIds, setOpenIds] = useState<string[]>(expandedIds);

  const onToggle = (value: string) => {
    if (!value) return;
    setOpenIds((prev) => {
      const newOpenIds = prev.includes(value)
        ? prev.filter((v) => v !== value)
        : [...prev, value];

      if (enableScrollIntoView && !prev.includes(value)) {
        const targetElement = accordionRefs.current[value];
        if (targetElement) {
          requestAnimationFrame(() => {
            targetElement.scrollIntoView({
              behavior: 'smooth',
            });
          });
        }
      }

      return newOpenIds;
    });
  };

  const renderContent = (content: string | React.ReactNode) => {
    if (!content) return null;

    if (typeof content === 'string') {
      return (
        <div
          className="text-body-1-fixed where-ul:list-ul where-ul:text-gray-700 where-span:text-inline-wrap where-ol:list-ol space-y-2"
          dangerouslySetInnerHTML={{ __html: content }}
        />
      );
    }

    return content;
  };

  return (
    <div
      className="space-y-4"
      {...createDataAttributes({ test: `${testId}-items` })}
      {...props}
    >
      {accordionMode === 'group' ? (
        <AccordionGroup
          data={items.map((item) => ({
            title: item.title,
            value: item.id,
            content: renderContent(item?.content),
          }))}
          defaultValue={expandedIds[0]}
          className="space-y-4"
          wrapperClassName="rounded-base-md overflow-hidden"
        />
      ) : (
        items.map((item, index) => {
          const isOpen = openIds.includes(item.id);

          return (
            <Accordion
              key={item.id}
              ref={(el) => {
                accordionRefs.current[item.id] = el;
              }}
              title={item.title}
              value={item.id}
              isOpen={isOpen}
              onChange={onToggle}
              wrapperClassName={cn('rounded-base-md overflow-hidden', {
                'scroll-mt-16': enableScrollIntoView,
              })}
              content={renderContent(item?.content)}
              {...createDataAttributes({
                test: `${testId}-item-${index}`,
              })}
            />
          );
        })
      )}
    </div>
  );
};
